/**
 * ThornhavenScene - Data-driven Thornhaven exterior environment
 * 
 * Norse-inspired settlement with:
 * - Terrain with grass and dirt paths
 * - Houses and the Golden Mead Hall
 * - Props (barrels, crates, fences)
 * - Trees and rocks
 * - Atmospheric lighting
 */

import { useMemo } from 'react';
import { useThree } from '@react-three/fiber';
import { useEffect } from 'react';
import { RigidBody, CuboidCollider } from '@react-three/rapier';
import * as THREE from 'three';
import { 
  Tree, 
  Rock 
} from './NorseBuildings';
import GoldenMeadHall from './GoldenMeadHall';

// Scene layout data - easily editable
const THORNHAVEN_LAYOUT = {
  terrain: {
    // Phase 3: Extended terrain to 150x150 to cover transition area to chunk system
    // This ensures no gap between Thornhaven and wilderness chunks
    // Chunks start rendering terrain at ~64 units from origin
    size: 150,
    grassColor: '#3D5C1F',
    grassColorAlt: '#4A7028',
    dirtColor: '#5C4332',
  },
  
  goldenMeadHall: {
    position: [28, 0, 0],
    rotation: [0, -Math.PI / 2, 0],
  },

  trees: [
    // Around the edges
    { position: [-35, 0, -20], scale: 1.2, variant: 0 },
    { position: [-38, 0, -5], scale: 1.0, variant: 1 },
    { position: [-36, 0, 10], scale: 1.3, variant: 2 },
    { position: [-32, 0, 25], scale: 1.1, variant: 0 },
    { position: [35, 0, -18], scale: 1.2, variant: 1 },
    { position: [38, 0, 5], scale: 1.0, variant: 2 },
    { position: [33, 0, 20], scale: 1.4, variant: 0 },
    { position: [-10, 0, -30], scale: 1.1, variant: 1 },
    { position: [15, 0, -32], scale: 1.3, variant: 2 },
    { position: [-5, 0, 38], scale: 1.2, variant: 0 },
    { position: [20, 0, 35], scale: 1.0, variant: 1 },
    // Some scattered inside
    { position: [-25, 0, -18], scale: 0.9, variant: 2 },
    { position: [28, 0, 15], scale: 0.8, variant: 0 },
  ],
  
  rocks: [
    { position: [-30, 0, -15], scale: 1.5, variant: 0 },
    { position: [-28, 0, -14], scale: 0.8, variant: 1 },
    { position: [32, 0, -12], scale: 1.2, variant: 2 },
    { position: [-33, 0, 8], scale: 1.0, variant: 0 },
    { position: [30, 0, 18], scale: 1.3, variant: 1 },
    { position: [-8, 0, -25], scale: 0.9, variant: 2 },
    { position: [5, 0, 32], scale: 1.1, variant: 0 },
  ],
  
  paths: [
    // Main road from south
    { start: [0, 35], end: [0, -35], width: 4 },
    // Path to Golden Mead Hall
    { start: [0, 5], end: [10, 8], width: 2.5 },
    // Path to houses
    { start: [0, 0], end: [-15, -5], width: 2 },
    { start: [-15, -5], end: [-20, 5], width: 2 },
  ],
  
  interactionZone: {
    position: [10, 0, 8],
    radius: 3,
  },
};

/**
 * Ground plane with grass texture variation
 * 
 * FIXED: Removed shadow-receiving from overlay patches to prevent
 * circular shadow artifacts (shadow acne on coplanar surfaces).
 * The base grass layer receives shadows; patches are blended visuals only.
 * Also using seeded random positions to prevent re-render flickering.
 */

// Seeded random for stable grass patch positions across re-renders
const seededRandom = (seed) => {
  const x = Math.sin(seed * 12.9898 + seed * 78.233) * 43758.5453;
  return x - Math.floor(x);
};

// Pre-computed grass patch data (stable across re-renders)
const GRASS_PATCHES = Array.from({ length: 15 }).map((_, i) => ({
  x: (seededRandom(i * 3 + 1) - 0.5) * 80,
  z: (seededRandom(i * 3 + 2) - 0.5) * 80,
  scale: 5 + seededRandom(i * 3 + 3) * 10,
  rotation: seededRandom(i * 3 + 4) * Math.PI,
}));

function Terrain({ size, grassColor, grassColorAlt, dirtColor }) {
  return (
    <group>
      {/* Main ground collision */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[size/2, 0.5, size/2]} position={[0, -0.5, 0]} />
      </RigidBody>
      
      {/* Grass base - this receives shadows */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow position={[0, 0, 0]}>
        <planeGeometry args={[size, size, 1, 1]} />
        <meshStandardMaterial color={grassColor} roughness={0.95} />
      </mesh>
      
      {/* REMOVED: Circular grass patches that were causing visual artifacts */}
      {/* These transparent circle overlays created the "circular glow" effect */}
    </group>
  );
}

/**
 * Dirt path/road
 * 
 * FIXED: Path is now flush with terrain (Y=0.001) and receives shadows.
 * Uses polygonOffset to prevent z-fighting with grass layer.
 */
function DirtPath({ start, end, width }) {
  const length = Math.sqrt(
    Math.pow(end[0] - start[0], 2) + Math.pow(end[1] - start[1], 2)
  );
  const angle = Math.atan2(end[0] - start[0], end[1] - start[1]);
  const midX = (start[0] + end[0]) / 2;
  const midZ = (start[1] + end[1]) / 2;
  
  return (
    <mesh 
      rotation={[-Math.PI / 2, 0, -angle]} 
      position={[midX, 0.001, midZ]}
      receiveShadow
    >
      <planeGeometry args={[width, length]} />
      <meshStandardMaterial 
        color="#5C4332" 
        roughness={0.95}
        polygonOffset
        polygonOffsetFactor={-1}
        polygonOffsetUnits={-1}
      />
    </mesh>
  );
}

/**
 * Interaction zone visual indicator
 * REMOVED: The ring geometry was causing circular artifacts.
 * Interaction zones are now invisible but still functional.
 */
function InteractionZone({ position, radius }) {
  // Invisible interaction zone - no visual ring
  return null;
}

/**
 * Main Thornhaven Scene Component
 */
export default function ThornhavenScene() {
  const { scene } = useThree();
  
  // Set up environment
  useEffect(() => {
    scene.background = new THREE.Color('#87CEEB');
    scene.fog = new THREE.Fog('#A8C4D4', 40, 100);
    return () => { scene.fog = null; };
  }, [scene]);
  
  const layout = THORNHAVEN_LAYOUT;
  
  return (
    <group>
      {/* Lighting - optimized to prevent shadow acne */}
      <ambientLight intensity={0.5} color="#FFF8E7" />
      <directionalLight 
        position={[30, 50, 20]} 
        intensity={1.0} 
        castShadow 
        shadow-mapSize={[1024, 1024]}
        shadow-camera-far={80}
        shadow-camera-near={10}
        shadow-camera-left={-40}
        shadow-camera-right={40}
        shadow-camera-top={40}
        shadow-camera-bottom={-40}
        shadow-bias={-0.001}
        shadow-normalBias={0.05}
        color="#FFF5E6"
      />
      {/* Fill light - no shadows, provides soft ambient fill */}
      <directionalLight position={[-20, 20, -10]} intensity={0.25} color="#B8D4E8" />
      
      {/* Terrain */}
      <Terrain {...layout.terrain} />
      
      {/* Dirt paths */}
      {layout.paths.map((path, i) => (
        <DirtPath key={`path-${i}`} {...path} />
      ))}
      
      {/* Golden Mead Hall - grand Gothic/Victorian/Norse-fantasy civic landmark */}
      <GoldenMeadHall 
        position={layout.goldenMeadHall.position} 
        rotation={layout.goldenMeadHall.rotation}
      />
      
      {/* Interaction zone */}
      <InteractionZone {...layout.interactionZone} />

      {/* Legacy clutter (decorative cabins, fences, barrels, crates) removed —
          Thornhaven is now assembled from the canonical World-Builder system. */}

      {/* Trees */}
      {layout.trees.map((tree, i) => (
        <Tree key={`tree-${i}`} {...tree} />
      ))}
      
      {/* Rocks */}
      {layout.rocks.map((rock, i) => (
        <Rock key={`rock-${i}`} {...rock} />
      ))}
      
      {/* Phase 3: Boundary walls REMOVED for open-world continuity */}
      {/* Players can now walk beyond the old 100x100 Thornhaven limits */}
      {/* The world chunk system provides terrain and collision beyond this area */}
    </group>
  );
}
